import { useQuery } from "@tanstack/react-query";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { queryClient } from "../../app/react-query-client";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  CollectionBuildSessionDetail,
  CollectionBuildSessionSummary,
  getCollectionBuildSession,
  listCollectionBuildSessions,
} from "../../app/api/collectionBuild";
import { RootState } from "../../app/store";
import {
  CLIENT_SSO_COLLECTION_BUILD_PATH,
  CLIENT_SSO_LIBRARY_PATH,
  isClientSsoUser,
} from "../../constants/clientSso";
import {
  clearCollectionBuildDraftSessionId,
  COLLECTION_BUILD_NOTIFICATIONS_LIST_LIMIT,
  COLLECTION_BUILD_NOTIFIED_KEYS_LIMIT,
  COLLECTION_BUILD_NOTIFIED_STORAGE_PREFIX,
  COLLECTION_BUILD_NOTIFICATION_STORAGE_PREFIX,
  COLLECTION_BUILD_PENDING_STORAGE_PREFIX,
  COLLECTION_BUILD_SESSION_PARAM,
  COLLECTION_BUILD_SESSION_POLL_MS,
  collectionBuildSummaryTitle,
  collectionBuildTitleFromSession,
  collectionSessionDisplayStatus,
  collectionSessionIsBusy,
  newestCollectionBuildOutput,
} from "./collectionBuildSession";
import {
  mergeCollectionBuildSessionList,
  pendingIdsFromSessionList,
  sortCollectionBuildSummariesByUpdatedAt,
} from "./collectionBuildSessionsListMerge";
import {
  COLLECTION_BUILD_SESSIONS_LIST_LIMIT,
  collectionBuildSessionsListQueryKey,
  GENERATIVE_PLATFORM_LIBRARY_STALE_MS,
} from "./generativePlatformQueryKeys";

export type CollectionBuildNotification = {
  id: number;
  title: string;
  status: "generated" | "failed";
  read: boolean;
  at: string;
};

type CollectionBuildSessionsValue = {
  summaries: CollectionBuildSessionSummary[];
  activeSession: CollectionBuildSessionDetail | null;
  pendingSessionIds: number[];
  notifications: CollectionBuildNotification[];
  unreadCount: number;
  /** Bumped by startNewDraft so CollectionBuild can empty the form even when already on CB. */
  newDraftNonce: number;
  /** Poll while moodboard/looks generate. Never call during query-form editing. */
  watchSession: (sessionId: number) => void;
  /** Load session detail once (Recents open / explicit). Does not start polling. */
  loadSession: (
    sessionId: number
  ) => Promise<CollectionBuildSessionDetail | null>;
  refreshSummaries: () => Promise<void>;
  /** Bump Recents order after a real user save (not open/hydrate). */
  touchSummaryUpdatedAt: (sessionId: number) => void;
  /**
   * After autosave: refresh Recents title from form fields (market/theme/…)
   * and bump updatedAt. Inserts a draft row when the session is new to the list.
   */
  upsertSummaryFields: (
    sessionId: number,
    fields: {
      market?: string | null;
      category?: string | null;
      season?: string | null;
      theme?: string | null;
      status?: string;
      skipMoodboard?: boolean;
      rangePlan?: CollectionBuildSessionSummary["rangePlan"];
    }
  ) => void;
  markRead: (sessionId: number) => void;
  markAllRead: () => void;
  openSession: (sessionId: number) => void;
  /** Sidebar Collection Build: clear draft ownership, leave results, empty query form. */
  startNewDraft: () => void;
};

const CollectionBuildSessionsContext =
  createContext<CollectionBuildSessionsValue | null>(null);

const readIds = (key: string): number[] => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as number[]) : [];
    return Array.from(
      new Set(parsed.filter(id => Number.isInteger(id) && id > 0))
    );
  } catch {
    return [];
  }
};

const readNotifications = (userId: string): CollectionBuildNotification[] => {
  try {
    const raw = localStorage.getItem(
      `${COLLECTION_BUILD_NOTIFICATION_STORAGE_PREFIX}:${userId}`
    );
    const parsed = raw
      ? (JSON.parse(raw) as CollectionBuildNotification[])
      : [];
    return Array.isArray(parsed)
      ? parsed.slice(0, COLLECTION_BUILD_NOTIFICATIONS_LIST_LIMIT)
      : [];
  } catch {
    return [];
  }
};

const readNotifiedKeys = (userId: string): Set<string> => {
  try {
    const raw = localStorage.getItem(
      `${COLLECTION_BUILD_NOTIFIED_STORAGE_PREFIX}:${userId}`
    );
    const parsed = raw ? (JSON.parse(raw) as string[]) : [];
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter(item => typeof item === "string" && item)
        : []
    );
  } catch {
    return new Set();
  }
};

/** One notification per session + terminal status + newest output (regen is a new output). */
const sessionNotifyKey = (session: CollectionBuildSessionDetail) => {
  const status = collectionSessionDisplayStatus(session);
  const latest = newestCollectionBuildOutput(session.outputs);
  return `${session.sessionId}:${status}:${latest?.id ?? 0}`;
};

/** Ready to leave the poll list: nothing in-flight and Recents status is terminal. */
const sessionSettled = (session: CollectionBuildSessionDetail) => {
  if (collectionSessionIsBusy(session)) {
    return false;
  }
  const status = collectionSessionDisplayStatus(session);
  return status === "generated" || status === "failed";
};

export const CollectionBuildSessionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useSelector(
    (state: RootState) => state.user.userInfo?.userId || ""
  );
  const group = useSelector(
    (state: RootState) => state.user.userInfo?.group || ""
  );
  const enabled = Boolean(userId && isClientSsoUser(group));
  // Session list for Recents (CB) and Library; skip fetch on IG / other routes.
  // Do not use CLIENT_SSO_HOME_PATH here — it aliases Image Generator.
  const onCollectionBuildPage =
    location.pathname.startsWith(CLIENT_SSO_COLLECTION_BUILD_PATH) ||
    location.pathname.startsWith(CLIENT_SSO_LIBRARY_PATH);

  const sessionsListQuery = useQuery({
    queryKey: collectionBuildSessionsListQueryKey(
      userId,
      COLLECTION_BUILD_SESSIONS_LIST_LIMIT
    ),
    queryFn: () =>
      listCollectionBuildSessions(COLLECTION_BUILD_SESSIONS_LIST_LIMIT, 0),
    enabled: enabled && onCollectionBuildPage && Boolean(userId),
    staleTime: GENERATIVE_PLATFORM_LIBRARY_STALE_MS,
  });

  const [summaries, setSummaries] = useState<CollectionBuildSessionSummary[]>(
    []
  );
  const [activeSession, setActiveSession] =
    useState<CollectionBuildSessionDetail | null>(null);
  const [pendingSessionIds, setPendingSessionIds] = useState<number[]>([]);
  const [notifications, setNotifications] = useState<
    CollectionBuildNotification[]
  >([]);
  const [newDraftNonce, setNewDraftNonce] = useState(0);
  const pendingRef = useRef<number[]>([]);
  const completingRef = useRef(new Set<number>());
  const seenStatusRef = useRef(new Map<number, string>());
  const notifiedKeysRef = useRef(new Set<string>());
  const awaitingFreshOutputRef = useRef(new Set<number>());
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const activeIdRef = useRef<number | null>(null);
  const notificationsRef = useRef<CollectionBuildNotification[]>([]);
  notificationsRef.current = notifications;
  const refreshSummariesRef = useRef<() => Promise<void>>(
    async () => undefined
  );

  const persistPending = useCallback((ids: number[]) => {
    const unique = Array.from(new Set(ids));
    pendingRef.current = unique;
    setPendingSessionIds(unique);
    const currentUserId = userIdRef.current;
    if (currentUserId) {
      localStorage.setItem(
        `${COLLECTION_BUILD_PENDING_STORAGE_PREFIX}:${currentUserId}`,
        JSON.stringify(unique)
      );
    }
  }, []);

  const persistNotifications = useCallback(
    (next: CollectionBuildNotification[]) => {
      setNotifications(next);
      const currentUserId = userIdRef.current;
      if (currentUserId) {
        localStorage.setItem(
          `${COLLECTION_BUILD_NOTIFICATION_STORAGE_PREFIX}:${currentUserId}`,
          JSON.stringify(next)
        );
      }
    },
    []
  );

  const persistNotifiedKeys = useCallback(() => {
    const currentUserId = userIdRef.current;
    if (!currentUserId) {
      return;
    }
    localStorage.setItem(
      `${COLLECTION_BUILD_NOTIFIED_STORAGE_PREFIX}:${currentUserId}`,
      JSON.stringify(
        Array.from(notifiedKeysRef.current).slice(
          -COLLECTION_BUILD_NOTIFIED_KEYS_LIMIT
        )
      )
    );
  }, []);

  const upsertSummary = useCallback((session: CollectionBuildSessionDetail) => {
    const looks = session.outputs.filter(item => item.outputType === "look");
    const moodboards = session.outputs.filter(
      item => item.outputType === "moodboard"
    );
    const latest = newestCollectionBuildOutput(session.outputs);
    const preview =
      moodboards.find(item => item.moodboardUrl || item.imageUrl) ||
      looks.find(item => item.imageUrl);
    const named = collectionBuildTitleFromSession(session);
    const lookCount = new Set(looks.map(item => item.outputNumber || item.id))
      .size;
    const displayStatus = collectionSessionDisplayStatus(session);
    setSummaries(prev => {
      const existing = prev.find(item => item.sessionId === session.sessionId);
      // Preserve list timestamps on open/poll so mere GET/load does not re-sort Recents.
      // Bump only when display status actually changes (e.g. generating → generated).
      const fallbackTs = latest?.createdAt || new Date().toISOString();
      const statusChanged =
        existing != null && existing.status !== displayStatus;
      const market = named.market || existing?.market || null;
      const category = named.category || existing?.category || null;
      const season = named.season || existing?.season || null;
      const theme = named.theme || existing?.theme || null;
      // Prefer computed Market · Theme / Market · Category; keep prior title if no inputs yet.
      const title =
        market || category || season || theme
          ? collectionBuildSummaryTitle({
              market,
              category,
              season,
              theme,
              lookCount,
              moodboardCount: moodboards.length,
              status: displayStatus,
            })
          : existing?.title || named.title;
      const summary: CollectionBuildSessionSummary = {
        sessionId: session.sessionId,
        status: displayStatus,
        title,
        season,
        market,
        category,
        theme,
        skipMoodboard: session.skipMoodboard,
        rangePlan: session.rangePlan,
        lookCount,
        moodboardCount: moodboards.length,
        previewUrl: preview?.moodboardUrl || preview?.imageUrl || null,
        createdAt: existing?.createdAt || fallbackTs,
        updatedAt: statusChanged
          ? new Date().toISOString()
          : existing?.updatedAt || fallbackTs,
      };
      return sortCollectionBuildSummariesByUpdatedAt([
        summary,
        ...prev.filter(item => item.sessionId !== session.sessionId),
      ]);
    });
  }, []);

  const touchSummaryUpdatedAt = useCallback((sessionId: number) => {
    if (!Number.isInteger(sessionId) || sessionId < 1) {
      return;
    }
    const now = new Date().toISOString();
    setSummaries(prev => {
      if (!prev.some(item => item.sessionId === sessionId)) {
        return prev;
      }
      return sortCollectionBuildSummariesByUpdatedAt(
        prev.map(item =>
          item.sessionId === sessionId ? { ...item, updatedAt: now } : item
        )
      );
    });
  }, []);

  const upsertSummaryFields = useCallback(
    (
      sessionId: number,
      fields: {
        market?: string | null;
        category?: string | null;
        season?: string | null;
        theme?: string | null;
        status?: string;
        skipMoodboard?: boolean;
        rangePlan?: CollectionBuildSessionSummary["rangePlan"];
      }
    ) => {
      if (!Number.isInteger(sessionId) || sessionId < 1) {
        return;
      }
      const now = new Date().toISOString();
      setSummaries(prev => {
        const existing = prev.find(item => item.sessionId === sessionId);
        const market =
          fields.market !== undefined
            ? fields.market || null
            : existing?.market || null;
        const category =
          fields.category !== undefined
            ? fields.category || null
            : existing?.category || null;
        const season =
          fields.season !== undefined
            ? fields.season || null
            : existing?.season || null;
        const theme =
          fields.theme !== undefined
            ? fields.theme || null
            : existing?.theme || null;
        const status = fields.status || existing?.status || "draft";
        const title = collectionBuildSummaryTitle({
          market,
          category,
          season,
          theme,
          lookCount: existing?.lookCount || 0,
          moodboardCount: existing?.moodboardCount || 0,
          status,
        });
        const summary: CollectionBuildSessionSummary = {
          sessionId,
          status,
          title,
          season,
          market,
          category,
          theme,
          skipMoodboard:
            fields.skipMoodboard ?? existing?.skipMoodboard ?? false,
          rangePlan:
            fields.rangePlan !== undefined
              ? fields.rangePlan
              : existing?.rangePlan || null,
          lookCount: existing?.lookCount || 0,
          moodboardCount: existing?.moodboardCount || 0,
          previewUrl: existing?.previewUrl || null,
          createdAt: existing?.createdAt || now,
          updatedAt: now,
        };
        return sortCollectionBuildSummariesByUpdatedAt([
          summary,
          ...prev.filter(item => item.sessionId !== sessionId),
        ]);
      });
    },
    []
  );

  const completeSession = useCallback(
    (session: CollectionBuildSessionDetail) => {
      const sessionId = session.sessionId;
      // Draft / pre-enqueue GETs have no terminal outputs — keep pending so a
      // follow-up watch after generate can poll until generated/failed.
      if (!sessionSettled(session)) {
        seenStatusRef.current.set(sessionId, "generating");
        return;
      }
      const status = collectionSessionDisplayStatus(session);
      const failed = status === "failed";
      const notifyStatus: CollectionBuildNotification["status"] = failed
        ? "failed"
        : "generated";
      const key = sessionNotifyKey(session);
      const prev = seenStatusRef.current.get(sessionId);
      const wasPending = pendingRef.current.includes(sessionId);
      const wasAwaiting = awaitingFreshOutputRef.current.has(sessionId);
      const alreadyNotified = notifiedKeysRef.current.has(key);

      // First GET after watchSession can still be the previous generated row.
      // Keep polling once; the next tick should show the new generating output.
      if (alreadyNotified && wasAwaiting) {
        awaitingFreshOutputRef.current.delete(sessionId);
        return;
      }

      awaitingFreshOutputRef.current.delete(sessionId);
      seenStatusRef.current.set(sessionId, notifyStatus);
      if (wasPending) {
        persistPending(pendingRef.current.filter(id => id !== sessionId));
      }
      completingRef.current.add(sessionId);

      if (alreadyNotified) {
        return;
      }
      notifiedKeysRef.current.add(key);
      persistNotifiedKeys();

      // Recents / remount / poll of an already-settled board: remember it, do not toast.
      const shouldNotify = prev === "generating" || wasAwaiting;
      if (!shouldNotify) {
        return;
      }

      const title = collectionBuildTitleFromSession(session).title;
      persistNotifications(
        [
          {
            id: sessionId,
            title,
            status: notifyStatus,
            read: false,
            at: new Date().toISOString(),
          },
          ...notificationsRef.current.filter(item => item.id !== sessionId),
        ].slice(0, COLLECTION_BUILD_NOTIFICATIONS_LIST_LIMIT)
      );
      // Error panel on the open session already explains timeout vs failure.
      // Never toast timeout/failure after a later output for this session succeeded.
      if (failed && activeIdRef.current !== sessionId) {
        toast.error(`${title} didn’t finish.`, { hideProgressBar: true });
      } else if (!failed) {
        toast.success(`${title} is ready. Open it from Recents.`, {
          hideProgressBar: true,
        });
      }
    },
    [persistNotifiedKeys, persistNotifications, persistPending]
  );

  const applySession = useCallback(
    (session: CollectionBuildSessionDetail) => {
      const sessionId = session.sessionId;
      const wasWatching =
        pendingRef.current.includes(sessionId) ||
        awaitingFreshOutputRef.current.has(sessionId) ||
        seenStatusRef.current.get(sessionId) === "generating";
      upsertSummary(session);
      if (activeIdRef.current === sessionId) {
        setActiveSession(session);
      }
      completeSession(session);
      // One list sync when a watched generate settles — not on open/hydrate.
      if (wasWatching && sessionSettled(session)) {
        void refreshSummariesRef.current();
      }
    },
    [completeSession, upsertSummary]
  );

  const loadSession = useCallback(
    async (sessionId: number) => {
      try {
        const session = await getCollectionBuildSession(sessionId);
        activeIdRef.current = sessionId;
        // Open/view: bind active detail + sync list fields, but do not bump updatedAt.
        setActiveSession(session);
        upsertSummary(session);
        completeSession(session);
        return session;
      } catch {
        return null;
      }
    },
    [completeSession, upsertSummary]
  );

  /** Poll generation progress. Do not call while the user is editing the query form. */
  const watchSession = useCallback(
    (sessionId: number) => {
      if (!Number.isInteger(sessionId) || sessionId < 1) {
        return;
      }
      completingRef.current.delete(sessionId);
      awaitingFreshOutputRef.current.add(sessionId);
      seenStatusRef.current.set(sessionId, "generating");
      persistPending([...pendingRef.current, sessionId]);
      activeIdRef.current = sessionId;
      setSummaries(prev => {
        if (!prev.some(item => item.sessionId === sessionId)) {
          return prev;
        }
        return prev.map(item =>
          item.sessionId === sessionId && item.status !== "generating"
            ? { ...item, status: "generating" }
            : item
        );
      });
      void loadSession(sessionId);
    },
    [loadSession, persistPending]
  );

  const applySessionListResult = useCallback(
    (result: Awaited<ReturnType<typeof listCollectionBuildSessions>>) => {
      setSummaries(prev => mergeCollectionBuildSessionList(prev, result));
      persistPending(
        pendingIdsFromSessionList(result, {
          pendingRef,
          awaitingFreshOutputRef,
          seenStatusRef,
        })
      );
    },
    [persistPending]
  );

  const refreshSummaries = useCallback(async () => {
    if (!enabled) {
      return;
    }
    try {
      await queryClient.refetchQueries({
        queryKey: collectionBuildSessionsListQueryKey(
          userId,
          COLLECTION_BUILD_SESSIONS_LIST_LIMIT
        ),
      });
    } catch {
      // History is optional until the first generate (empty CB tables).
    }
  }, [enabled, userId]);
  refreshSummariesRef.current = refreshSummaries;

  const markRead = useCallback(
    (sessionId: number) => {
      persistNotifications(
        notifications.map(item =>
          item.id === sessionId ? { ...item, read: true } : item
        )
      );
    },
    [notifications, persistNotifications]
  );

  const markAllRead = useCallback(() => {
    persistNotifications(notifications.map(item => ({ ...item, read: true })));
  }, [notifications, persistNotifications]);

  const openSession = useCallback(
    (sessionId: number) => {
      markRead(sessionId);
      navigate(
        `${CLIENT_SSO_COLLECTION_BUILD_PATH}?${COLLECTION_BUILD_SESSION_PARAM}=${sessionId}`
      );
    },
    [markRead, navigate]
  );

  /** Sidebar "Collection Build": drop cbSession, abandon in-memory draft, empty form. */
  const startNewDraft = useCallback(() => {
    clearCollectionBuildDraftSessionId();
    activeIdRef.current = null;
    setActiveSession(null);
    setNewDraftNonce(nonce => nonce + 1);
    navigate(CLIENT_SSO_COLLECTION_BUILD_PATH, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!enabled || !userId) {
      setSummaries([]);
      setActiveSession(null);
      pendingRef.current = [];
      setPendingSessionIds([]);
      setNotifications([]);
      completingRef.current.clear();
      seenStatusRef.current.clear();
      notifiedKeysRef.current.clear();
      awaitingFreshOutputRef.current.clear();
      activeIdRef.current = null;
      return;
    }
    pendingRef.current = readIds(
      `${COLLECTION_BUILD_PENDING_STORAGE_PREFIX}:${userId}`
    );
    setPendingSessionIds(pendingRef.current);
    setNotifications(readNotifications(userId));
    notifiedKeysRef.current = readNotifiedKeys(userId);
    completingRef.current.clear();
    seenStatusRef.current.clear();
    awaitingFreshOutputRef.current.clear();
  }, [enabled, userId]);

  useEffect(() => {
    if (sessionsListQuery.data) {
      applySessionListResult(sessionsListQuery.data);
    }
  }, [applySessionListResult, sessionsListQuery.data]);

  useEffect(() => {
    if (!enabled || !pendingSessionIds.length) {
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      if (document.visibilityState === "hidden" || cancelled) {
        return;
      }
      const ids = [...pendingRef.current];
      try {
        const sessions = (
          await Promise.all(
            ids.map(id => getCollectionBuildSession(id).catch(() => null))
          )
        ).filter((item): item is CollectionBuildSessionDetail => Boolean(item));
        if (!cancelled) {
          sessions.forEach(applySession);
        }
      } catch {
        // Keep the last known sessions if a poll fails.
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void tick();
      }
    };
    const timer = window.setInterval(() => {
      void tick();
    }, COLLECTION_BUILD_SESSION_POLL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [applySession, enabled, pendingSessionIds.length]);

  const unreadCount = useMemo(
    () => notifications.filter(item => !item.read).length,
    [notifications]
  );

  const value = useMemo(
    () => ({
      summaries,
      activeSession,
      pendingSessionIds,
      notifications,
      unreadCount,
      newDraftNonce,
      watchSession,
      loadSession,
      refreshSummaries,
      touchSummaryUpdatedAt,
      upsertSummaryFields,
      markRead,
      markAllRead,
      openSession,
      startNewDraft,
    }),
    [
      activeSession,
      loadSession,
      markAllRead,
      markRead,
      newDraftNonce,
      notifications,
      openSession,
      pendingSessionIds,
      refreshSummaries,
      startNewDraft,
      summaries,
      touchSummaryUpdatedAt,
      unreadCount,
      upsertSummaryFields,
      watchSession,
    ]
  );

  return (
    <CollectionBuildSessionsContext.Provider value={value}>
      {children}
    </CollectionBuildSessionsContext.Provider>
  );
};

export const useCollectionBuildSessions = () => {
  const value = useContext(CollectionBuildSessionsContext);
  if (!value) {
    throw new Error("CollectionBuildSessionsProvider is required.");
  }
  return value;
};

export const useCollectionBuildSessionsOptional = () =>
  useContext(CollectionBuildSessionsContext);
